import type { GraphQuery, GraphQueryResponse, TreeNode } from '@/types';

/**
 * Entity type derived from GraphQueryResponse keys (excluding metadata)
 * Single source of truth - no hardcoded lists
 */
export type EntityType = keyof Omit<GraphQueryResponse, 'count' | 'offset'>;

/**
 * RelationshipNode type from TreeNode (matches structure from API)
 */
interface RelationshipNode {
  label: string;
  target?: {
    result?: {
      ports?: Array<{
        key: string;
        port: number;
        protocol: string;
        service?: string;
      }>;
      vulnerabilities?: Array<{
        key: string;
        epss?: number;
        cvss?: number;
        kev?: boolean;
        title?: string;
        exploit?: boolean;
      }>;
    };
  };
}

/**
 * Result structure containing both data array and entity type
 */
export interface QueryResults {
  data: unknown[];
  type: EntityType;
}

/**
 * Extracts query results from GraphQueryResponse and identifies entity type
 *
 * @param response - GraphQueryResponse from API
 * @returns Object with data array and type, or null if no valid data
 *
 * @example
 * const response = { assets: [...], count: 2 };
 * const result = extractQueryResults(response);
 * // Returns: { data: [...], type: 'assets' }
 */
export function extractQueryResults(
  response: GraphQueryResponse | undefined
): QueryResults | null {
  if (!response) {
    return null;
  }

  for (const [key, value] of Object.entries(response)) {
    // Skip metadata fields
    if (key === 'count' || key === 'offset') {
      continue;
    }

    // Check if value is an array of objects
    if (
      Array.isArray(value) &&
      value.length > 0 &&
      typeof value[0] === 'object' &&
      value[0] !== null
    ) {
      return {
        data: value,
        type: key as EntityType,
      };
    }
  }

  return null;
}

/**
 * Checks if a query has relationships defined
 *
 * @param query - GraphQuery to check
 * @returns true if query has at least one relationship
 *
 * @example
 * const query = { node: { relationships: [{ label: 'HAS_PORT', ... }] } };
 * hasRelationships(query); // true
 */
export function hasRelationships(query: GraphQuery | undefined): boolean {
  if (!query) return false;
  return !!query.node?.relationships && query.node.relationships.length > 0;
}

/**
 * Deduplicates entities by their key field
 * Prevents duplicate rows when graph queries with OPTIONAL relationships return
 * the same entity multiple times (once per matched relationship)
 *
 * Based on pattern from useAssets.tsx lines 814-826
 *
 * @param data - Array of entities to deduplicate
 * @returns Deduplicated array keeping first occurrence of each key
 *
 * @example
 * const duplicates = [{ key: 'a', val: 1 }, { key: 'a', val: 2 }, { key: 'b', val: 3 }];
 * deduplicateByKey(duplicates); // Returns [{ key: 'a', val: 1 }, { key: 'b', val: 3 }]
 */
function deduplicateByKey<T extends Record<string, unknown>>(data: T[]): T[] {
  const seenKeys = new Set<string>();
  return data.filter(item => {
    const key = item.key;
    if (typeof key !== 'string') return true; // Keep items without string keys
    if (seenKeys.has(key)) return false; // Skip duplicate
    seenKeys.add(key);
    return true; // Keep first occurrence
  });
}

/**
 * Transforms TreeNode[] response to flat array with relationship data joined
 * Used for table queries that have relationships (e.g., HAS_PORT)
 *
 * Based on the pattern from useAssets.tsx lines 791-810
 *
 * @param trees - TreeNode array from API (when tree: true is enabled)
 * @returns Flat array of entities with relationships merged and deduplicated
 *
 * @example
 * const trees = [{ node: { result: { assets: [...] }, relationships: [...] } }];
 * transformTreeNodeToFlat(trees); // Returns flat array with ports joined
 */
export function transformTreeNodeToFlat(
  trees: TreeNode[] | undefined | null
): unknown[] {
  if (!trees || !Array.isArray(trees)) {
    return [];
  }

  const result = trees
    .map((tree): unknown | null => {
      // Extract first entity array from result
      const entities = Object.values(tree?.node?.result || {}).find(e =>
        Array.isArray(e)
      ) as unknown[];

      // Extract ports from HAS_PORT relationships (PR #3413 pattern)
      // When filtering by port/protocol, the Port nodes are returned in relationships
      const ports = (
        tree?.node?.relationships as RelationshipNode[] | undefined
      )
        ?.filter((rel: RelationshipNode) => rel.label === 'HAS_PORT')
        .flatMap((rel: RelationshipNode) => rel.target?.result?.ports || [])
        .filter(Boolean);

      // Safe access to first entity
      const firstEntity = entities?.[0];
      if (!firstEntity) {
        return null;
      }

      // Extract vulnerability enrichment from INSTANCE_OF relationships
      // Risk -> INSTANCE_OF -> Vulnerability: merge epss/cvss/kev/title into parent Risk
      const vulnerability = (
        tree?.node?.relationships as RelationshipNode[] | undefined
      )
        ?.filter((rel: RelationshipNode) => rel.label === 'INSTANCE_OF')
        .flatMap(
          (rel: RelationshipNode) => rel.target?.result?.vulnerabilities || []
        )?.[0] as
        | {
            epss?: number;
            cvss?: number;
            kev?: boolean;
            title?: string;
            exploit?: boolean;
          }
        | undefined;

      // Merge entity with extracted relationship data
      return {
        ...firstEntity,
        // Include ports from relationships (takes precedence over entity's ports field)
        ...(ports && ports.length > 0 && { ports }),
        // Cherry-pick vulnerability fields to avoid key/id/username collision with parent Risk
        ...(vulnerability && {
          epss: vulnerability.epss,
          cvss: vulnerability.cvss,
          kev: vulnerability.kev,
          title: vulnerability.title,
          exploit: vulnerability.exploit,
        }),
      };
    })
    .filter((entity): entity is unknown => entity !== null);

  // BUGFIX: Deduplicate entities by key to prevent duplicate rows in table
  // This handles cases where graph queries with OPTIONAL relationships return
  // the same entity multiple times (once per matched relationship).
  // Example: Asset with 2 HAS_PORT relationships returns 2 rows from Neo4j.
  // Related: Applying pattern from useAssets.tsx lines 814-826
  return deduplicateByKey(result as Record<string, unknown>[]);
}
